import { X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Input } from '@apps/web/components/ui/input';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { assert } from '@libraries/typescript/utilities';

type ShiftGroup = {
    readonly shift_ids: ReadonlyArray<string>;
    readonly group_mode: 'all' | 'any';
};

type DemoCallButtonProps = {
    readonly selectedShiftIds: Array<string>;
    readonly instructions?: string;
    readonly openingLine?: string;
    readonly payRate?: string;
    readonly recurrenceScheduleDescription?: string;
    readonly phoebeIntro?: string;
    readonly isDisabled?: boolean;
    readonly agentType:
        | 'callout'
        | 'recurring_shift'
        | 'clock_in'
        | 'clock_out'
        | 'survey';
    readonly shiftGroups?: ReadonlyArray<ShiftGroup>;
};

export function DemoCallButton({
    selectedShiftIds,
    instructions,
    openingLine,
    payRate,
    recurrenceScheduleDescription,
    phoebeIntro,
    isDisabled,
    agentType,
    shiftGroups,
}: DemoCallButtonProps) {
    const [showInput, setShowInput] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isCallInProgress, setIsCallInProgress] = useState(false);

    const organizationId = useOrganizationId();

    const hasShifts = selectedShiftIds.length > 0;

    async function handleDemoCall() {
        assert.isNotNull(organizationId);

        if (!hasShifts) {
            toast.error(
                'Please select a shift before requesting a test call.',
                { persistNotification: false },
            );
            return;
        }

        if (!phoneNumber.trim()) {
            toast.error('Please enter a phone number.', {
                persistNotification: false,
            });
            return;
        }

        setIsCallInProgress(true);
        try {
            const body: {
                phone_number: string;
                agent_notes: string;
                shift_ids: Array<string>;
                opening_line?: string;
                pay_rate?: string;
                recurrence_schedule_description?: string;
                phoebe_intro?: string;
                agent_type: typeof agentType;
                shift_groups?: Array<{
                    shift_ids: Array<string>;
                    group_mode: 'all' | 'any';
                }>;
            } = {
                phone_number: phoneNumber,
                agent_notes: instructions || '',
                shift_ids: selectedShiftIds,
                agent_type: agentType,
            };

            if (openingLine?.trim()) {
                body.opening_line = openingLine.trim();
            }

            if (payRate?.trim()) {
                body.pay_rate = payRate.trim();
            }

            if (recurrenceScheduleDescription?.trim()) {
                body.recurrence_schedule_description =
                    recurrenceScheduleDescription.trim();
            }

            if (phoebeIntro?.trim()) {
                body.phoebe_intro = phoebeIntro.trim();
            }

            if (shiftGroups && shiftGroups.length > 0) {
                body.shift_groups = shiftGroups.map((g) => ({
                    shift_ids: [...g.shift_ids],
                    group_mode: g.group_mode,
                }));
            }

            await getApiClient().post({
                url: '/organizations/{organization_id}/voice-playground/create-call',
                pathParams: { organization_id: organizationId },
                body,
            });

            toast.success('Demo call initiated successfully', {
                persistNotification: false,
            });
        } catch (error) {
            console.error('Error creating demo call:', error);
            toast.error('Failed to initiate demo call', {
                persistNotification: false,
            });
        } finally {
            setIsCallInProgress(false);
        }
    }

    if (!showInput) {
        return (
            <Button
                type="button"
                variant="outline"
                onClick={() => setShowInput(true)}
                disabled={isDisabled || !hasShifts}
            >
                Receive Test Call
            </Button>
        );
    }

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-1">
                <Input
                    placeholder="Your phone number"
                    className="w-40"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={isCallInProgress}
                />
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleDemoCall}
                    disabled={
                        !phoneNumber.trim() || isCallInProgress || !hasShifts
                    }
                >
                    {isCallInProgress ? 'Call In Progress...' : 'Call'}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowInput(false)}
                    className="text-muted-foreground border"
                    disabled={isCallInProgress}
                >
                    <X />
                </Button>
            </div>
        </div>
    );
}
